import { Navigate, useLocation } from "react-router-dom";
import { useAuth, AppRole } from "@/contexts/AuthContext";
import { useModuleVisibility } from "@/hooks/useModuleVisibility";
import { Skeleton } from "@/components/ui/skeleton";
import { AccessDenied } from "./AccessDenied";

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: AppRole[];
}

/** Routes that should show the AccessDenied screen (with allowed-roles preflight)
 *  instead of silently redirecting on denial. */
const PREFLIGHT_PATHS = new Set<string>(["/attendance"]);

const ProtectedRoute = ({ children, allowedRoles }: ProtectedRouteProps) => {
  const { user, roles, loading } = useAuth();
  const { isUrlVisible, isLoading: isVisibilityLoading } = useModuleVisibility(roles);
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="space-y-4 w-64 text-center">
          <Skeleton className="h-10 w-10 rounded-full mx-auto" />
          <Skeleton className="h-4 w-48 mx-auto" />
          <p className="text-sm text-muted-foreground">Verifying session…</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;

  if (isVisibilityLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="space-y-4 w-64 text-center">
          <Skeleton className="h-10 w-10 rounded-full mx-auto" />
          <Skeleton className="h-4 w-48 mx-auto" />
          <p className="text-sm text-muted-foreground">Checking access…</p>
        </div>
      </div>
    );
  }

  if (allowedRoles && allowedRoles.length > 0) {
    const hasRoleAccess = allowedRoles.some((r) => roles.includes(r));
    const hasDynamicAccess = isUrlVisible(location.pathname);
    if (!hasRoleAccess && !hasDynamicAccess) {
      if (PREFLIGHT_PATHS.has(location.pathname)) {
        return (
          <AccessDenied
            pathname={location.pathname}
            allowedRoles={allowedRoles}
            userRoles={roles}
          />
        );
      }
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
